import React, { useState, useEffect } from 'react';
import { API_URL } from '../config';

function ProductCard({ mockup, onShowBanner }) {
  const [selectedImage, setSelectedImage] = useState(0);
  const [publishing, setPublishing] = useState(false);
  const images = mockup.images || [];
  
  // Show first 6 thumbnails
  const thumbnails = images.slice(0, 6);

  const handleOrder = async () => {

    console.log('');
    console.log('═══════════════════════════════════════════════════════════');
    console.log('🛒 ORDER FLOW STARTED');
    console.log('═══════════════════════════════════════════════════════════');
    console.log('Mockup data:', mockup);
    console.log('  product_id:', mockup.product_id);
    console.log('  name:', mockup.name);
    console.log('  type:', mockup.type);
    console.log('  images:', mockup.images?.length || 0, 'images');

    if (!mockup.product_id) {
      console.error('❌ No product_id found in mockup:', mockup);
      onShowBanner('❌ Product ID not available. Please try generating mockups again.', 'error');
      return;
    }

    setPublishing(true);

    try {
      // Step 1: Publish to Shopify
      console.log('');
      console.log('📍 STEP 1: Publishing to Shopify');
      console.log('🔗 API Endpoint:', `${API_URL}/publish`);
      console.log('📤 Request Method: POST');
      console.log('📋 Request Body:', JSON.stringify({ product_id: mockup.product_id }, null, 2));

      const response = await fetch(`${API_URL}/publish`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ product_id: mockup.product_id }),
      });

      console.log('');
      console.log('📡 Response received from worker');
      console.log('  Status Code:', response.status);
      console.log('  Status Text:', response.statusText);
      console.log('  Headers:', Array.from(response.headers.entries()).map(([k, v]) => `${k}: ${v}`));

      const data = await response.json();
      console.log('');
      console.log('📊 Response Data:');
      console.log('  Full Response:', JSON.stringify(data, null, 2));
      console.log('  Success:', data.success);
      console.log('  Error:', data.error);
      console.log('  External Object:', JSON.stringify(data.external));
      console.log('  External Handle:', data.external?.handle);
      console.log('  Shop URL:', data.shop_url);
      console.log('  Message:', data.message);
      console.log('  All Response Keys:', Object.keys(data));

      if (data.error) {
        console.warn('⚠️ API returned error:', data.error);
        throw new Error(data.error);
      }

      // Open Shopify product page
      const storeUrl = data.external?.handle || data.shop_url;
      console.log('🔗 Extracted storeUrl:', storeUrl);

      if (storeUrl) {
        console.log('✅ Store URL ready:', storeUrl);

        onShowBanner('✅ Product published! Redirecting to Shopify...', 'success');
        window.location.href = storeUrl;
      } else {
        console.error('❌ No storeUrl found in response');
        throw new Error('No store URL returned');
      }
    } catch (err) {
      console.error('❌ Order error:', err);
      onShowBanner(`❌ Failed: ${err.message}`, 'error');
    } finally {
      setPublishing(false);
    }
  };

  if (mockup.error) {
    return (
      <div className="product-card error-card">
        <div className="product-error">
          <p>⚠️ {mockup.name}</p>
          <p className="error-detail">{mockup.error}</p>
        </div>
      </div>
    );
  }

  if (!images.length) {
    return (
      <div className="product-card loading-card">
        <div className="product-placeholder">
          <div className="skeleton-img" />
          <div className="skeleton-text" />
        </div>
      </div>
    );
  }

  return (
    <div className="product-card">
      {/* Main Product Image */}
      <div className="product-image-container">
        <img
          src={typeof images[selectedImage] === 'string' ? images[selectedImage] : images[selectedImage]?.src}
          alt={`${mockup.name} - View ${selectedImage + 1}`}
          className="product-main-image"
        />
        {images.length > 1 && (
          <div className="image-counter">
            {selectedImage + 1} / {images.length}
          </div>
        )}
      </div>

      {/* Thumbnail Gallery */}
      {thumbnails.length > 1 && (
        <div className="product-thumbnails">
          {thumbnails.map((img, idx) => (
            <button
              key={idx}
              className={`thumbnail ${selectedImage === idx ? 'active' : ''}`}
              onClick={() => setSelectedImage(idx)}
            >
              <img src={typeof img === 'string' ? img : img?.src} alt={`View ${idx + 1}`} />
            </button>
          ))}
          {images.length > 6 && (
            <button 
              className="thumbnail more"
              onClick={() => setSelectedImage(6)}
            >
              +{images.length - 6}
            </button>
          )}
        </div>
      )}

      {/* Product Info */}
      <div className="product-info">
        <h3 className="product-name">{mockup.name}</h3>
        <p className="product-type">{mockup.type}</p>
        <div className="product-meta">
          <span className="mockup-count">📸 {images.length} views</span>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="product-actions">
        <button
          className="btn-primary btn-order"
          onClick={handleOrder}
          disabled={publishing}
          title="Buy this product"
        >
          {publishing ? '⏳ Opening store...' : '🛒 Buy Now'}
        </button>
      </div>
    </div>
  );
}

function RealProducts({ imageUrl, designId, originalImage }) {
  const [mockups, setMockups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [banner, setBanner] = useState(null);
  const [loadedDesignId, setLoadedDesignId] = useState(null);

  const showBanner = (message, type = 'info') => {
    setBanner({ message, type });
    setTimeout(() => setBanner(null), 5000);
  };

  useEffect(() => {

    if (!imageUrl) {
      setMockups([]);
      setLoadedDesignId(null);
      return;
    }

    // Skip if we already have mockups for this design (variant switch)
    if (designId && designId === loadedDesignId && mockups.length > 0) {
      console.log('📸 Skipping mockup fetch — already loaded for design', designId);
      return;
    }

    // Debounce to avoid duplicate calls from rapid state changes (React StrictMode, races)
    let cancelled = false;
    const timer = setTimeout(() => {
      if (cancelled) return;

      setLoading(true);
      setError(null);

      console.log('');
      console.log('═══════════════════════════════════════════════════════════');
      console.log('📸 MOCKUP GENERATION STARTED');
      console.log('═══════════════════════════════════════════════════════════');

      const requestBody = { image_url: imageUrl };

      // Use designId only for the original image, not for variants
      const isOriginalImage = originalImage && imageUrl === originalImage;
      console.log(`🎨 Image type: ${isOriginalImage ? 'Original (First generated)' : 'Variant (User selected)'}`);
      console.log(`📍 Image ID: ${imageUrl.split('/').pop()}`);
      if (designId && isOriginalImage) {
        requestBody.design_id = designId;
        console.log(`✅ Design ID provided: ${designId}`);
        console.log(`🔍 Looking for cached products...`);
      } else if (designId) {
        // Always send design_id if available (e.g. loaded from URL)
        requestBody.design_id = designId;
        console.log(`✅ Design ID provided: ${designId}`);
        console.log(`🔍 Looking for cached products...`);
      } else {
        console.log(`ℹ️ No design ID, caching by image_url`);
      }

      console.log('🔗 API Endpoint:', `${API_URL}/mockups`);
      console.log('📤 Request Method: POST');
      console.log('📋 Request Body:', JSON.stringify(requestBody, null, 2));

      fetch(`${API_URL}/mockups`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestBody),
      })
        .then(res => {
          if (cancelled) return;
          return res.json();
        })
        .then(data => {
          if (cancelled || !data) return;
        if (data.mockups) {
          const count = data.mockups.length;
          const printifyStatus = data.cached
            ? '🎉 Products ALREADY EXIST in Printify'
            : '⚙️ Products being CREATED in Printify';

          console.log(`\n${'='.repeat(60)}`);
          console.log(`✅ Loaded ${count} product(s) from Printify`);
          console.log(`${printifyStatus}`);
          console.log(`${'='.repeat(60)}`);

          // Log details for each product
          data.mockups.forEach((mockup, idx) => {
            console.log(`\n📦 Product ${idx + 1}: ${mockup.name}`);
            console.log(`   Type: ${mockup.type}`);
            console.log(`   Printify ID: ${mockup.product_id}`);
            console.log(`   Mockup images: ${mockup.images?.length || 0}`);
            if (mockup.error) {
              console.warn(`   ⚠️ Error: ${mockup.error}`);
            }
          });

          console.log(`\n${'='.repeat(60)}`);
          if (data.cached) {
            console.log('ℹ️ Click "Buy Now" to publish to your Shopify store');
          } else {
            console.log('ℹ️ Products will be created on Printify when you click "Buy Now"');
          }
          console.log(`${'='.repeat(60)}\n`);

          setMockups(data.mockups);
          if (designId) setLoadedDesignId(designId);
        } else if (data.error) {
          setError(data.error);
        }
        setLoading(false);
      })
      .catch(err => {
        if (cancelled) return;
        console.error('Mockup generation error:', err);
        setError('Failed to generate mockups');
        setLoading(false);
      });
    }, 300); // 300ms debounce

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [imageUrl, designId]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!imageUrl) {
    return (
      <div className="real-products-empty">
        <div className="empty-state">
          <div className="empty-icon">🎨</div>
          <h3>No Design Selected</h3>
          <p>Generate a design or select one from the gallery to see it on products!</p>
          <div className="empty-preview">
            <div className="preview-box">👕</div>
            <div className="preview-box">🧥</div>
            <div className="preview-box">☕</div>
            <div className="preview-box">🧢</div>
            <div className="preview-box">🖼️</div>
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="real-products-loading">
        <div className="loading-content">
          <div className="loading-spinner" />
          <h3>Creating Your Products...</h3>
          <p>Generating professional mockups</p>
          <div className="loading-steps">
            <span className="step">📤 Uploading design</span>
            <span className="step">📦 Creating products</span>
            <span className="step">📸 Rendering mockups</span>
          </div>
          <p className="loading-time">This usually takes 10-15 seconds</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="real-products-error">
        <div className="error-content">
          <div className="error-icon">⚠️</div>
          <h3>Mockup Generation Failed</h3>
          <p>{error}</p>
          <button 
            className="btn-retry"
            onClick={() => window.location.reload()}
          >
            🔄 Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="real-products-container">
      {/* Banner */}
      {banner && (
        <div className={`banner banner-${banner.type}`}>
          <div className="banner-content">
            <span>{banner.message}</span>
            <button className="banner-close" onClick={() => setBanner(null)}>✕</button>
          </div>
        </div>
      )}

      {/* Product Grid */}
      <div className="products-grid">
        {mockups.map((mockup, idx) => (
          <ProductCard key={idx} mockup={mockup} onShowBanner={showBanner} />
        ))}
      </div>

      {/* Footer Info */}
      {mockups.length > 0 && (
        <div className="products-footer">
          <div className="footer-stats">
            <span>✅ {mockups.length} products</span>
            <span>📸 {mockups.reduce((sum, m) => sum + (m.images?.length || 0), 0)} mockup images</span>
            <span>⚡ Ready to order</span>
          </div>
          <p className="footer-note">
            Click "Order This" to customize size, color, and complete your purchase through Printify
          </p>
        </div>
      )}
    </div>
  );
}

export default RealProducts;
